/** Stable NPC identity, not mood, skill, or a player's relationship state.
 * V1 recipes are immutable: add named fields without changing existing draws;
 * changing an existing recipe requires an explicit version/migration. */
import { hashSeed } from "../rng.js";

export const NPC_PERSONALITY_VERSION = 1;
export const NPC_PERSONALITY_FACETS = [
  "sociability", "assertiveness", "compassion", "politeness", "diligence", "curiosity", "candor", "stressSensitivity",
] as const;
export type NpcPersonalityFacet = typeof NPC_PERSONALITY_FACETS[number];
export type NpcDispositions = Readonly<Record<NpcPersonalityFacet, number>>;
export const NPC_PERSONALITY_VALUES = ["mastery", "loyalty", "fairness", "security", "autonomy", "recognition"] as const;
export type NpcPersonalValue = typeof NPC_PERSONALITY_VALUES[number];

export const NPC_INTERESTS = {
  tools: { label: "old tools", brief: "Old tools. I like seeing how things fit together.", detail: "Even a simple hinge can give you something to puzzle over." },
  gardens: { label: "gardens", brief: "Gardens. I like watching things grow.", detail: "There's always some small change to notice." },
  cooking: { label: "cooking", brief: "Cooking. I like how a small change can transform a meal.", detail: "You can keep learning from the simplest ingredients." },
  music: { label: "music", brief: "Music. A good tune gives me something to think about.", detail: "I like noticing the little variations when someone plays it again." },
  stories: { label: "stories", brief: "Stories. I like hearing how people see things.", detail: "The part someone chooses to tell first can be interesting." },
  walking: { label: "quiet walks", brief: "A quiet walk. It gives me room to think.", detail: "Sometimes I notice more when I have nowhere particular to be." },
  stars: { label: "the night sky", brief: "The night sky. I like looking for patterns.", detail: "There's something pleasant about having so much left to understand." },
  games: { label: "table games", brief: "Table games. I enjoy a little friendly competition.", detail: "I like the moment when a move changes the whole game." },
} as const;
export type NpcInterest = keyof typeof NPC_INTERESTS;
export const NPC_HABITS = ["considers_before_answering", "small_nod", "open_hands", "brief_glance_away"] as const;
export type NpcHabit = typeof NPC_HABITS[number];

export const NPC_RESPONSE_PATTERNS = {
  deflect_public_praise: "Redirects public praise toward the work; can accept appreciation privately.",
  share_credit: "Acknowledges others when praised, without inventing who helped.",
  welcome_praise: "Accepts appreciation directly.",
  teach_through_questions: "Responds to questions by inviting a closer look.",
  practical_answers: "Prefers a concrete answer before a longer explanation.",
  help_with_boundaries: "Offers available help while making limits clear.",
} as const;
export type NpcResponsePattern = keyof typeof NPC_RESPONSE_PATTERNS;
export interface NpcExpressionStyle {
  readonly length: "brief" | "conversational";
  readonly manner: "plain" | "gentle" | "dry";
  readonly gesture: "restrained" | "open";
}

export interface NpcPersonality {
  readonly version: 1;
  readonly dispositions: NpcDispositions;
  /** Ordered: the first priority has greater weight. */
  readonly values: readonly [NpcPersonalValue, NpcPersonalValue];
  readonly patterns: readonly [NpcResponsePattern, NpcResponsePattern];
  readonly interest: NpcInterest;
  readonly habit: NpcHabit;
  readonly expression: NpcExpressionStyle;
  readonly provenance: {
    readonly recipe: "npc-personality-v1";
    readonly identity: readonly string[];
    readonly motif: string;
    readonly legacyTraits: readonly string[];
    /** Community influence on legacy traits, never evidence of witnessing an event. */
    readonly communityRef?: string;
    readonly authoredRef?: string;
  };
}

/** Authoring input only. Runtime events must not write into the baseline. */
export interface NpcPersonalityOverrides {
  readonly ref: string;
  readonly dispositions?: Partial<NpcDispositions>;
  readonly values?: readonly [NpcPersonalValue, NpcPersonalValue];
  readonly interest?: NpcInterest;
  readonly habit?: NpcHabit;
  readonly expression?: Partial<NpcExpressionStyle>;
}

interface Motif {
  id: string;
  dispositions: Partial<NpcDispositions>;
  values: readonly [NpcPersonalValue, NpcPersonalValue];
}
// Occupation-independent combinations, with wide individual variation. These
// are authored game distributions, not claims about human trait correlations.
const MOTIFS: readonly Motif[] = [
  { id: "independent_helper", dispositions: { compassion: 68, sociability: 34, assertiveness: 62 }, values: ["autonomy", "loyalty"] },
  { id: "welcoming_host", dispositions: { sociability: 72, compassion: 62, politeness: 68 }, values: ["loyalty", "recognition"] },
  { id: "exacting_maker", dispositions: { diligence: 74, assertiveness: 64, curiosity: 62 }, values: ["mastery", "autonomy"] },
  { id: "careful_steward", dispositions: { diligence: 65, stressSensitivity: 65, candor: 60 }, values: ["security", "fairness"] },
  { id: "curious_explorer", dispositions: { curiosity: 76, sociability: 52, stressSensitivity: 34 }, values: ["autonomy", "mastery"] },
  { id: "ambitious_negotiator", dispositions: { sociability: 65, assertiveness: 72, politeness: 62, candor: 36 }, values: ["recognition", "security"] },
];
const TRAIT_LEANS: Readonly<Record<string, Partial<NpcDispositions>>> = {
  gruff: { politeness: -12, sociability: -8 }, cheerful: { sociability: 12, stressSensitivity: -6 },
  wary: { stressSensitivity: 10, sociability: -8 }, curious: { curiosity: 14 },
  weary: { sociability: -5 }, proud: { assertiveness: 10 }, kind: { compassion: 14 },
  sly: { candor: -12 }, devout: { diligence: 5 }, restless: { curiosity: 8 },
  careful: { diligence: 10, stressSensitivity: 5 }, resourceful: { curiosity: 10, assertiveness: 5 },
  steady: { diligence: 8, stressSensitivity: -10 }, sociable: { sociability: 14 }, bold: { assertiveness: 14 },
  measured: { stressSensitivity: -10, politeness: 8 }, protective: { compassion: 10, assertiveness: 8 },
  incisive: { assertiveness: 10, candor: 8 },
};
const bound = (n: number): number => Math.max(0, Math.min(100, Math.round(n)));

/** Full actor identity components, e.g. ["region", planetScopedNpcId] or
 * ["settlement", beaconId, residentId]. Names/appearance/time are not inputs.
 * Canonical definitions deliberately use their authored definition identity. */
export function generateNpcPersonality(input: {
  identity: readonly string[];
  traits: readonly string[];
  communityRef?: string;
  overrides?: NpcPersonalityOverrides;
}): NpcPersonality {
  if (!input.identity.length || input.identity.some(part => !part)) throw new Error("Personality requires a full actor identity");
  const seed = JSON.stringify(["npc-personality-v1", ...input.identity]);
  const unit = (field: string): number => hashSeed(JSON.stringify([seed, field])) / 0x100000000;
  const choose = <T>(field: string, items: readonly T[]): T => items[Math.floor(unit(field) * items.length)];
  const motif = choose("motif", MOTIFS);
  const traits = [...new Set(input.traits)].sort();
  const overrides = input.overrides;
  const dispositions = {} as Record<NpcPersonalityFacet, number>;
  for (const facet of NPC_PERSONALITY_FACETS) {
    const lean = traits.reduce((sum, trait) => sum + (TRAIT_LEANS[trait]?.[facet] ?? 0), 0);
    // Independent named draws prevent newly added fields shifting existing ones.
    const variation = (unit(`facet:${facet}:a`) + unit(`facet:${facet}:b`) - 1) * 30;
    const authored = overrides?.dispositions?.[facet];
    if (authored !== undefined && (!Number.isFinite(authored) || authored < 0 || authored > 100)) {
      throw new Error(`Invalid authored personality facet: ${facet}`);
    }
    dispositions[facet] = authored === undefined
      ? bound((motif.dispositions[facet] ?? 50) + variation + Math.max(-14, Math.min(14, lean)))
      : bound(authored);
  }
  const valueScores: Record<NpcPersonalValue, number> = {
    mastery: dispositions.diligence + dispositions.curiosity,
    loyalty: dispositions.compassion + dispositions.sociability,
    fairness: dispositions.compassion + dispositions.candor,
    security: dispositions.stressSensitivity + dispositions.diligence,
    autonomy: dispositions.assertiveness + 100 - dispositions.sociability,
    recognition: dispositions.assertiveness + dispositions.sociability,
  };
  const ranked = NPC_PERSONALITY_VALUES.map(value => ({ value, score: valueScores[value] * 0.15
    + unit(`value:${value}`) * 45 + (motif.values[0] === value ? 30 : motif.values[1] === value ? 18 : 0) }))
    .sort((a, b) => b.score - a.score || (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
  const values: readonly [NpcPersonalValue, NpcPersonalValue] = overrides?.values ?? [ranked[0].value, ranked[1].value];
  if (values[0] === values[1] || values.some(value => !NPC_PERSONALITY_VALUES.includes(value))) {
    throw new Error("Personality needs two distinct registered priorities");
  }
  const praise: NpcResponsePattern = values[0] === "autonomy" && dispositions.sociability < 60
    ? "deflect_public_praise" : dispositions.compassion >= 60 && values.includes("loyalty")
      ? "share_credit" : "welcome_praise";
  const questions: NpcResponsePattern = dispositions.curiosity >= 60
    ? "teach_through_questions" : dispositions.compassion >= 65 ? "help_with_boundaries" : "practical_answers";
  const expression: NpcExpressionStyle = {
    length: overrides?.expression?.length ?? (dispositions.sociability < 45 ? "brief" : "conversational"),
    // Manner and gesture have their own draws: reserve does not dictate pitch,
    // reading speed, kindness, or constant downcast eye contact.
    manner: overrides?.expression?.manner ?? choose("expression:manner", ["plain", "gentle", "dry"] as const),
    gesture: overrides?.expression?.gesture ?? choose("expression:gesture", ["restrained", "open"] as const),
  };
  return Object.freeze({
    version: NPC_PERSONALITY_VERSION,
    dispositions: Object.freeze(dispositions), values: Object.freeze([...values] as [NpcPersonalValue, NpcPersonalValue]),
    patterns: Object.freeze([praise, questions] as [NpcResponsePattern, NpcResponsePattern]),
    interest: overrides?.interest ?? choose("interest", Object.keys(NPC_INTERESTS) as NpcInterest[]),
    habit: overrides?.habit ?? choose("habit", NPC_HABITS), expression: Object.freeze(expression),
    provenance: Object.freeze({ recipe: "npc-personality-v1", identity: Object.freeze([...input.identity]),
      motif: motif.id, legacyTraits: Object.freeze(traits),
      ...(input.communityRef ? { communityRef: input.communityRef } : {}),
      ...(overrides ? { authoredRef: overrides.ref } : {}),
    }),
  });
}

/** Compact player-facing description; no hidden relationship claims or stats. */
export function npcPersonalitySummary(profile: NpcPersonality): string {
  const d = profile.dispositions;
  const social = d.sociability < 40 ? "Reserved" : d.sociability > 65 ? "Sociable" : "Easy to talk to";
  const quality = d.assertiveness > 65 ? "self-assured" : d.compassion > 65 ? "considerate"
    : d.curiosity > 65 ? "inquisitive" : d.diligence > 65 ? "methodical" : "thoughtful";
  return `${social} and ${quality}. Values ${profile.values[0]}; enjoys ${NPC_INTERESTS[profile.interest].label}.`;
}

export interface NpcPersonalitySituation {
  kind: "praise" | "question" | "request";
  audience: "private" | "public" | "unknown";
  pressure: "calm" | "busy" | "unknown";
  /** Supplied by an encounter's knowledge/relationship adapter, never generated. */
  reliability: "unknown" | "reliable" | "unreliable";
}
export interface NpcPersonalityResponse {
  approach: "deflect" | "acknowledge" | "invite_question" | "answer_directly" | "clarify_request" | "set_boundary";
  reason: NpcResponsePattern | "time_pressure" | "uncertain_commitment";
}

/** A preference for an expressive move, not authorization to transact or a
 * promise to help. The caller still filters actions and grounds the reply. */
export function npcPersonalityResponse(profile: NpcPersonality, situation: NpcPersonalitySituation): NpcPersonalityResponse {
  if (situation.kind === "praise") {
    if (situation.audience === "public" && profile.patterns.includes("deflect_public_praise")) {
      return { approach: "deflect", reason: "deflect_public_praise" };
    }
    return { approach: "acknowledge", reason: profile.patterns.includes("share_credit") ? "share_credit" : "welcome_praise" };
  }
  if (situation.kind === "request") {
    if (situation.reliability === "unreliable") return { approach: "clarify_request", reason: "uncertain_commitment" };
    if (situation.pressure === "busy") return { approach: "set_boundary", reason: "time_pressure" };
    return { approach: "clarify_request", reason: profile.patterns[1] };
  }
  if (situation.pressure === "busy") return { approach: "answer_directly", reason: "time_pressure" };
  return profile.patterns.includes("teach_through_questions")
    ? { approach: "invite_question", reason: "teach_through_questions" }
    : { approach: "answer_directly", reason: profile.patterns[1] };
}
